import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIError } from '@data';
import { attributesKeys, metadataKeys } from '@data/constants';
import {
  AttributesApiGetAttributesRequest,
  LoginView,
  MetadataView,
  UpdatePasswordBody,
  UpdateProfileBody,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';

export const useUpdatePassword = (): UseMutationResult<
  LoginView,
  APIError,
  UpdatePasswordBody,
  unknown
> => {
  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.profile.updatePassword({ updatePasswordBody: values });
      return response.data;
    },
  });
};

export const useUpdateUser = (
  filters: AttributesApiGetAttributesRequest,
  transformViewToData: (val: UpdateProfileBody) => { answer: string; translationKey: string }[],
): UseMutationResult<void, APIError, UpdateProfileBody, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.profile.updateProfile({ updateProfileBody: values });
      return response.data;
    },
    onMutate: (values) => {
      const previousAttributes = queryClient.getQueryData(attributesKeys.list(filters));

      const transformedData = transformViewToData(values);

      queryClient.setQueryData(attributesKeys.list(filters), {
        items: transformedData,
      });

      return { previousAttributes };
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(attributesKeys.list(filters), context?.previousAttributes);
    },
  });
};

export const useUpdateUserDetails = (): UseMutationResult<
  void,
  APIError<UpdateProfileBody>,
  UpdateProfileBody,
  unknown
> =>
  useMutation({
    mutationFn: async (values) => {
      const response = await MatchingApi.profile.updateProfile({ updateProfileBody: values });
      return response.data;
    },
  });

export const useUserMetadata = () =>
  useQuery<MetadataView, Error>({
    queryKey: metadataKeys.all(),
    queryFn: async () => {
      const response = await MatchingApi.profile.getMetadata();
      return response.data;
    },
  });
